import i18n from "@/i18n";
import { f, type FilterDef, type FilterOption } from "@/composables/listing";
import { InvoiceStatus } from "@/data/enums/invoice";
import { FraudStatusMap } from "@/data/fraud";
import { FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR } from "@/data/table";

// ── Fraud status options (static, resolved once) ──
export function fraudStatusOptions() {
  const statuses = FraudStatusMap();
  return Object.values(statuses)
    .sort((a, b) => Number(a.id) - Number(b.id))
    .map(s => ({ value: String(s.id), label: String(s.label) }));
}

/** Flatten category tree to flat FilterOption[] */
export function flattenCategories(
  tree: any[]
): { value: string; label: string }[] {
  const result: { value: string; label: string }[] = [];
  const walk = (nodes: any[], prefix = "") => {
    for (const node of nodes) {
      const label = prefix
        ? `${prefix} → ${node.label || node.name}`
        : node.label || node.name;
      result.push({ value: node.id, label });
      if (node.children?.length) walk(node.children, label);
    }
  };
  walk(tree);
  return result;
}

export interface BuildOrderFiltersInput {
  isAdmin: boolean;
  isClient: boolean;
  /** When set, client-scoped filters are suppressed (single-client view). */
  clientId?: string | null;
  /**
   * Lazy resolver for tag picker options. Invoked by useListing on demand
   * (filter modal open or URL-restored active filter). Implementation should
   * await its data fetch then return mapped FilterOption[].
   */
  resolveTagOptions: () => Promise<FilterOption[]>;
  /**
   * Lazy resolver for the category tree (vue-treeselect). Invoked the same
   * way as resolveTagOptions; returns nested FilterOption[] with `children`.
   */
  resolveCategoryOptions: () => Promise<FilterOption[]>;
  /** Custom basket FilterDefs already built by useCustomFields().filtersFor. */
  customBasketFilters: FilterDef[];
  /** Custom client FilterDefs already built by useCustomFields().filtersFor. */
  customClientFilters: FilterDef[];
}

/**
 * Build the full FilterDef list for the orders listing. Pure: takes its
 * dependencies as an explicit input object so it can be unit-tested and
 * reused outside the composable.
 */
export function buildOrderFilters(input: BuildOrderFiltersInput): FilterDef[] {
  const {
    isAdmin,
    isClient,
    clientId,
    resolveTagOptions,
    resolveCategoryOptions,
    customBasketFilters,
    customClientFilters
  } = input;

  return [
    // ── Order ──
    f.text("id", {
      label: i18n.t("_.order__id"),
      group: "order",
      if: () => isAdmin
    }),
    f.text("number", {
      label: i18n.t("_.order_number"),
      group: "order"
    }),
    f.number("total_amount", {
      label: i18n.t("_.order_total"),
      group: "order"
    }),
    f.select("status.code", {
      label: i18n.t("_.order_status"),
      group: "order",
      multi: true,
      allowMultiple: false,
      operators: ["eq", "neq"],
      options: [
        { value: InvoiceStatus.PAID, label: i18n.t("_.paid").toString() },
        {
          // Alias bucket: UNPAID and ADJUSTED share a single "Unpaid" option.
          // The `&&` separator marks values that belong to the same bucket
          // so reconstructCheckboxValue (helpers/table.ts) can rebuild them.
          value: [InvoiceStatus.UNPAID, InvoiceStatus.ADJUSTED].join(
            FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR
          ),
          label: i18n.t("_.unpaid").toString()
        },
        {
          value: InvoiceStatus.OVERDUE,
          label: i18n.t("_.overdue").toString()
        },
        {
          value: InvoiceStatus.CANCELLED,
          label: i18n.t("_.cancelled").toString()
        },
        {
          value: InvoiceStatus.REFUNDED,
          label: i18n.t("_.refunded").toString()
        }
      ]
    }),
    f.date("created_at", {
      label: i18n.t("_.order_date_purchased"),
      group: "order"
    }),
    f.date("paid_datetime", {
      label: i18n.t("_.order_date_paid"),
      group: "order"
    }),
    f.select("fraud_status", {
      label: i18n.t("_.order_fraud_status"),
      group: "order",
      multi: true,
      options: fraudStatusOptions(),
      if: () => isAdmin
    }),

    // ── Order items ──
    f.text("products.product.name", {
      label: i18n.t("_.order_item_name"),
      group: "order_item"
    }),
    f.text("products.product.category.name", {
      label: i18n.t("_.order_item_category_name"),
      group: "order_item",
      if: () => isClient
    }),
    f.text("products.service_identifier", {
      label: i18n.t("_.order_item_service_identifier"),
      group: "order_item"
    }),
    // Category options resolve lazily on first use (modal open or URL-restored
    // active filter), so we don't need an eager fetch to know the count.
    f.category("products.product.category_id", {
      label: i18n.t("_.order_item_category"),
      group: "order_item",
      options: resolveCategoryOptions,
      if: () => isAdmin
    }),
    f.tag("products.tags.id", {
      label: i18n.t("_.order_item_tag").toString(),
      group: "order_item",
      options: resolveTagOptions,
      if: () => isAdmin
    }),
    ...customBasketFilters,

    // ── Client (admin only, no clientId) ──
    f.text("client.username", {
      label: i18n.t("_.client_username"),
      group: "client",
      if: () => isAdmin && !clientId
    }),
    f.select("client.has_login", {
      label: i18n.t("_.client_has_login"),
      group: "client",
      options: [
        { value: "1", label: i18n.t("_.yes").toString() },
        { value: "0", label: i18n.t("_.no").toString() }
      ],
      if: () => isAdmin && !clientId
    }),
    f.text("client.firstname", {
      label: i18n.t("_.client_firstname"),
      group: "client",
      if: () => isAdmin && !clientId
    }),
    f.text("client.lastname", {
      label: i18n.t("_.client_lastname"),
      group: "client",
      if: () => isAdmin && !clientId
    }),
    f.date("client.created_at", {
      label: i18n.t("_.client_created"),
      group: "client",
      if: () => isAdmin && !clientId
    }),
    f.text("client.id", {
      label: i18n.t("_.client_id"),
      group: "client",
      if: () => isAdmin && !clientId
    }),
    f.select("client.verified", {
      label: i18n.t("_.client_verified"),
      group: "client",
      options: [
        { value: "1", label: i18n.t("_.yes").toString() },
        { value: "0", label: i18n.t("_.no").toString() }
      ],
      if: () => isAdmin && !clientId
    }),
    f.select("client.fraud_status", {
      label: i18n.t("_.client_fraud_status"),
      group: "client",
      multi: true,
      options: fraudStatusOptions(),
      if: () => isAdmin && !clientId
    }),
    f.tag("client.tags.id", {
      label: i18n.t("_.client_tag").toString(),
      group: "client",
      options: resolveTagOptions,
      if: () => isAdmin && !clientId
    }),
    ...customClientFilters
  ];
}
